// MobileVodCard — card de filme/série/canal para a superfície MOBILE (touch).
//
// Parede A3. Reusa SÓ o visual/tokens do VodCard da TV (poster vertical,
// barra de progresso, título). NÃO importa Norigin / useFocusable: aqui não
// há foco de controle remoto. Interação é tap puro, com feedback de press
// via `:active` (scale/opacity no CSS) — não foco persistente.
//
// Renderiza como <button> nativo pra ganhar de graça: teclado nativo (Enter),
// acessibilidade (role/foco do teclado do SO), e alvo de toque generoso.
// Coração tapável (favoritar) é opcional via `onToggleFavorite` — só aparece
// quando o caller passa o handler.

import { Poster } from '../components/Poster'

import styles from './MobileVodCard.module.css'

export interface MobileVodCardProps {
  /** Título exibido abaixo do poster. */
  title: string
  /** URL do poster (vertical). `null` cai no placeholder de letra do Poster. */
  poster: string | null
  /** Tap no card. */
  onPress: () => void
  /** 0-100. Quando > 0, renderiza a barra de "continuar assistindo". */
  progress?: number | undefined
  /** Ano — exibido como badge discreto sobre o poster quando presente. */
  year?: number | null | undefined
  /** Distingue filme/série/canal (badge no canto + formato do poster). */
  kind?: 'vod' | 'series' | 'channel' | undefined
  /** Estado de favorito. Quando o handler abaixo existe, mostra o coração. */
  favorite?: boolean | undefined
  /** Handler do coração tapável. Ausente = sem botão de favoritar. */
  onToggleFavorite?: (() => void) | undefined
  /** Rótulo acessível do botão de favoritar (i18n no caller). */
  favoriteLabel?: string | undefined
}

export function MobileVodCard({
  title,
  poster,
  onPress,
  progress,
  year,
  kind,
  favorite,
  onToggleFavorite,
  favoriteLabel,
}: MobileVodCardProps): JSX.Element {
  const pct = typeof progress === 'number' ? Math.max(0, Math.min(100, progress)) : 0
  // Canal usa logo (quadrado), filme/série usa poster vertical.
  const posterKind = kind === 'channel' ? 'logo' : 'poster'

  return (
    <div className={styles.card} data-kind={kind ?? 'vod'}>
      <button
        type="button"
        className={styles.tap}
        onClick={onPress}
        // O título já vai no <span> visível abaixo; aria-label garante o
        // nome no botão mesmo que o layout mude. Touch alvo >= 44px (CSS).
        aria-label={title}
      >
        <span className={styles.posterWrap}>
          <Poster
            src={poster}
            kind={posterKind}
            alt={title}
            fallbackText={title}
            className={styles.poster}
            placeholderClassName={`${styles.poster} ${styles.placeholder}`}
          />
          {year != null && <span className={styles.yearBadge}>{year}</span>}
          {pct > 0 && (
            <span className={styles.progressTrack} aria-hidden="true">
              <span className={styles.progressFill} style={{ width: `${pct}%` }} />
            </span>
          )}
        </span>
        <span className={styles.title}>{title}</span>
      </button>

      {onToggleFavorite && (
        <button
          type="button"
          className={styles.heart}
          data-active={favorite ? 'true' : 'false'}
          aria-pressed={favorite ? 'true' : 'false'}
          aria-label={favoriteLabel ?? title}
          onClick={(e) => {
            // Não dispara o tap do card (navegação) ao favoritar.
            e.stopPropagation()
            onToggleFavorite()
          }}
        >
          {favorite ? '♥' : '♡'}
        </button>
      )}
    </div>
  )
}
